import {
	createContext,
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import Modal from 'react-modal';
import Select, { components, Theme, StylesConfig } from 'react-select';
import { useSelector } from 'react-redux';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useIsTablet from '../../helpers/useIsTablet';
import { DeviceNameType, getDevice } from '../../helpers/deviceHelper';

import styles from './SelectInput.module.scss';
import { getIsRTL } from '../../redux/LanguageSlice/LanguageSelectors';
import TestIds, { formatTestId } from '../../tests/TestIds';

const messages = defineMessages({
	noResult: {
		id: 'payments.no_results',
		defaultMessage: 'No results found',
	},
	placeholder: {
		id: 'payments.search_placeholder',
		defaultMessage: 'Search',
	},
});

type Option = {
	label: string;
	value: string;
};

const selectStyles: StylesConfig<Option, false> = {
	control: (provided) => ({
		...provided,
		boxShadow: 'none',
		border: 'none',
		background: 'transparent',
		color: '#102D6F',
		fontSize: '12px',
		display: 'flex',
		cursor: 'pointer',
		position: 'absolute',
		width: '100%',
	}),
	input: (provided, props) => ({
		...provided,
		display: 'flex',
		visibility: 'visible',
		cursor: 'pointer',
		fontSize: '14px',
		height: '30px',
		zIndex: 50,
		marginLeft: '10px',
		marginRight: '10px',
		marginTop: '12px',
		// eslint-disable-next-line @typescript-eslint/dot-notation
		textAlign: props['isRtl'] ? 'right' : 'left',
		// eslint-disable-next-line @typescript-eslint/dot-notation
		padding: props['isRtl'] ? '5px 5px 5px 30px' : '5px 30px 5px 5px',
		borderRadius: '3px',
		color: 'black',
		border: '1px solid #D9DBE5',
		pointerEvents: 'none',
	}),
	valueContainer: (provided) => ({
		...provided,
		padding: '0',
		minWidth: '155px',
		cursor: 'pointer',
		height: '45px',
		textAlign: 'right',
		alignItem: 'center',
		flexFlow: 'row wrap',
	}),
	indicatorSeparator: () => ({
		display: 'none',
	}),
	dropdownIndicator: (prodived) => ({
		...prodived,
		display: 'none',
	}),
	menu: (provided) => ({
		...provided,
		background: '#ffffff',
		borderRadius: '4px',
		padding: '0',
		zIndex: '10',
		cursor: 'pointer',
		textAlign: 'left',
		border: '0',
		paddingTop: '45px',
	}),
	menuList: (provided) => ({
		...provided,
		background: '#ffffff',
		cursor: 'pointer',
		padding: '0',
		border: '0',
	}),
};

const selectThemeColors = {
	primary: '#4972f4',
	primary25: '#f5f8ff',
	primary50: '#f5f8ff',
	primary75: '#4972f4',
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type SelectComponentProps<T = {}> = {
	data: {
		label: string;
		value: string;
	} & T;
	// eslint-disable-next-line react/no-unused-prop-types
	innerProps: any;
};

const optionFilter = ({ label, value }: Option, input: string) =>
	input
		.split(' ')
		.every((inputToken) =>
			[label, value].some((searchToken) =>
				new RegExp(inputToken.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i').test(
					searchToken
				)
			)
		);

type MobileSelectModalProps = {
	options: Option[];
	selected: string;
	onChangeSelect: (val: Option) => void;
	setIsSelectOpen: (isOpen: boolean) => void;
	componentOverrides: {
		Option: FC<SelectComponentProps>;
	};
};

const MobileSelectModal: FC<MobileSelectModalProps> = ({
	options,
	selected,
	onChangeSelect,
	setIsSelectOpen,
	componentOverrides: { Option },
}) => {
	const { formatMessage } = useIntl();
	const [nameFilter, setNameFilter] = useState<string>('');
	const deviceOS = getDevice();

	const onClick = (val: Option) => {
		onChangeSelect(val);
		setIsSelectOpen(false);
	};

	const closeModal = () => {
		setIsSelectOpen(false);
	};

	return (
		<div>
			<div className={styles.closeIcon} onClick={() => closeModal()} />
			<div
				className={classNames(
					styles.mobileInputContainer,
					deviceOS === DeviceNameType.ANDROID && styles.android
				)}
			>
				{deviceOS !== DeviceNameType.ANDROID && (
					<input
						className={styles.searchInput}
						placeholder={formatMessage(messages.placeholder)}
						onChange={(event: any) => setNameFilter(event.target.value)}
						type="text"
					/>
				)}
			</div>
			<ul className={styles.mobileSelect}>
				{options
					.filter((o: Option) => optionFilter(o, nameFilter))
					.map((o: Option) => {
						return (
							<li
								key={o.value}
								className={classNames({
									[styles.selected]: selected === o.value,
								})}
								onClick={() => onClick(o)}
							>
								<Option data={o} innerProps={{}} />
							</li>
						);
					})}
			</ul>
		</div>
	);
};

type SelectContextType = {
	isOpen: boolean;
	options: Option[];
};

const SelectContext = createContext<SelectContextType>({
	isOpen: false,
	options: [],
});

const SearchInput = ({ ...props }: any) => {
	const { formatMessage } = useIntl();
	const { isOpen } = useContext(SelectContext);

	if (!isOpen) return null;
	return (
		<div
			className={styles.inputContainer}
			onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
		>
			<div className={styles.inputGroup}>
				<components.Input
					{...props}
					id="searchInput"
					data-cy={TestIds.SelectInput}
					className={styles.searchInput}
					placeholder={formatMessage(messages.placeholder)}
				/>
			</div>
		</div>
	);
};

const MenuList = ({ ...props }: any) => {
	return (
		<div data-cy={TestIds.SelectInputOptions}>
			<components.MenuList {...props} />
		</div>
	);
};

const SelectOption = ({ innerProps, data: { label } }: SelectComponentProps) => {
	return (
		<div
			className={styles.selectOption}
			data-cy={formatTestId(TestIds.SelectInputOption_0, label)}
			{...innerProps}
		>
			<span>{label}</span>
		</div>
	);
};

const SelectedOption = ({ data }: SelectComponentProps) => {
	const { label } = data || {};

	return <span className={styles.selectedOption}>{label}</span>;
};

type SelectInputProps = {
	label?: string;
	labelClassName?: string;
	value: string | null;
	onChange: (value: string) => void;
	componentOverrides?: {
		Option?: FC<SelectComponentProps>;
		SingleValue?: FC<SelectComponentProps>;
	};
	options: Option[];
};

const SelectInput: FC<SelectInputProps> = ({
	value,
	onChange,
	label,
	labelClassName,
	componentOverrides = {},
	options,
}) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const selectRef = useRef<any /* types are garbage on this version */>(null);

	const isTablet = useIsTablet();
	const isRTL = useSelector(getIsRTL);

	useOnClickOutside(containerRef, () => {
		setIsOpen(false);
	});

	const selectValue = useMemo(
		() => options.find((x) => x.value === value) || options[0],
		[options, value]
	);

	const onSelectChange = useCallback(
		({ value: newValue }: any) => {
			setIsOpen(false);
			onChange(newValue!);
		},
		[onChange]
	);

	useEffect(() => {
		if (!value) onSelectChange(selectValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		if (isOpen) selectRef.current?.focus();
	}, [isOpen]);

	return (
		// eslint-disable-next-line react/jsx-no-constructed-context-values
		<SelectContext.Provider value={{ isOpen, options }}>
			<div className={styles.host} data-cy={TestIds.SelectInput}>
				{label && (
					<label className={classNames(styles.label, labelClassName)}>{label}</label>
				)}
				<div className={styles.container}>
					<div
						className={classNames(styles.selectedValue, {
							[styles.selectActive]: isOpen,
						})}
						ref={!isTablet ? containerRef : undefined}
						onClick={() => setIsOpen((current) => !current)}
						data-cy={TestIds.SelectValue}
					>
						{(componentOverrides.SingleValue && (
							<componentOverrides.SingleValue data={selectValue} innerProps={{}} />
						)) || <SelectedOption data={selectValue} innerProps={{}} />}
					</div>
					{isTablet && (
						<Modal isOpen={isOpen} className={styles.modal}>
							<MobileSelectModal
								options={options}
								selected={selectValue?.value}
								onChangeSelect={(val: any) => onSelectChange(val)}
								setIsSelectOpen={setIsOpen}
								componentOverrides={{
									Option: componentOverrides?.Option || SelectOption,
								}}
							/>
						</Modal>
					)}
					{isOpen && !isTablet && (
						<Select
							ref={selectRef}
							noOptionsMessage={() => formatMessage(messages.noResult)}
							value={selectValue}
							onChange={onSelectChange}
							options={options}
							isSearchable
							isClearable={false}
							components={{
								// Option: AutoConvertCurrencyOption,
								...componentOverrides,
								Input: SearchInput,
								SingleValue: () => null,
								MenuList,
							}}
							menuIsOpen={isOpen}
							filterOption={({ data }, input) => optionFilter(data, input)}
							styles={selectStyles}
							classNamePrefix="select"
							isRtl={isRTL}
							theme={(theme: Theme) => ({
								...theme,
								colors: {
									...theme.colors,
									...selectThemeColors,
								},
							})}
						/>
					)}
				</div>
			</div>
		</SelectContext.Provider>
	);
};

export default SelectInput;
